/**
 * Section: Newsletter Modal
 * ------------------------------------------------------------------------------
 * Newsletter modal configuration for complete theme support
 * - https://github.com/Shopify/theme-scripts/tree/master/packages/theme-sections
 *
 * @namespace newsletterModal
 */

import log from 'salvo-lite/log'

import { register } from '@shopify/theme-sections'

import debounce from 'lodash-es/debounce'
import cssClasses from '../helpers/cssClasses'
import { on } from '../helpers/utils'
import ScrollDirection from '../mixins/scroll-direction'
import breakpoints from '../helpers/breakpoints'

class SiteHeader {
    constructor () {
        this._selectors = {
            announcement: {
                container: '[data-announcement-banner="container"]',
                slide: '[data-announcement-banner="slide"]'
            },
            container: '[data-site-header-container]',
            main: '[data-header-container]',
            topBar: '[data-header-top]',
            singleAnnouncement: '.announcement',
            banners: '[data-banners="container"]'
        }

        /**
         * Global variables
         */
        this._announcementBannerCarousel = {}
        this._currentHeight = 0

        log.debug(this.constructor.name, 'Constructed', this)
    }

    onInit (theme, section) {
        register('site-header', {

            /**
             * Callback function when the section is loaded in via the 'sections.load()' function or by Theme Editor
             * 'shopify:section:unload' event
             */
            onLoad () {
                this.setNodeSelectors()
                this.constructComponents()
                this.init()
            },

            /**
             * Component this.section._selectors.
             */
            setNodeSelectors () {
                this.section = theme.getSectionsByType('site-header')

                this.topBar = document.querySelector(this.section._selectors.topBar)
                this.main = document.querySelector(this.section._selectors.main)

                this.banners = document.querySelector(this.section._selectors.banners)
                this.announcementContainer = document.querySelector(this.section._selectors.announcement.container)
                this.singleAnnouncement = document.querySelector(this.section._selectors.singleAnnouncement)
                this.announcementBanner = document.querySelector(this.section._selectors.announcement.container)
            },

            /**
             * Instantiate announcement component.
             */
            constructAnnouncement () {
                this.announcementBannerCarousel = theme.getComponentsById('announcement-banner')._carousel

                /**
                 * Initiate carousel on small screens if there are more than 1 item.
                 */
                if (this.getAnnouncementSlides() > 1 && !window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
                    this.announcementBannerCarousel.resize()
                } else {
                    this.announcementBannerCarousel.destroy()
                }
            },

            /**
             * Get total number of announcement slides.
             */
            getAnnouncementSlides () {
                return [...document.querySelectorAll(this.section._selectors.announcement.slide)].length
            },

            /**
             * Refresh the announcement carousel on resize.
             */
            refreshAnnouncementCarousel () {
                if (this.getAnnouncementSlides() <= 1) {
                    return
                }

                if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
                    if (this.announcementBannerCarousel) {
                        this.announcementBannerCarousel.destroy()
                    }
                }

                this.announcementBannerCarousel.resize()
            },

            /**
             * Instantiate a collapsed header on scroll using scrollDirection helper.
             */
            constructStickyHeader () {
                this.AnimatedHeader = ScrollDirection({
                    throttle: 250,
                    start: 100
                })
            },

            /**
             * Construct section components.
             */
            constructComponents () {
                this.constructAnnouncement()
                this.constructStickyHeader()
            },

            /**
             * Initialise component.
             */
            init () {
                this.setListeners()
                this.setStickyHeader()
            },

            /**
             * Set sticky header events after document has loaded styling.
             */
            setStickyHeader () {
                on('load', () => {
                    this.AnimatedHeader.init()
                    this.setBodyPadding()
                    section.parentNode.classList.add(cssClasses.sticky)
                })
            },

            /**
             * Set the padding on body element to site-header height.
             * @param {*} response
             */
            setBodyPadding (response) {
                const height = this.calculateHeaderHeight(response)
                this.section._currentHeight = height

                document.body.style.paddingTop = `${height}px`
            },

            /**
             * Calculate body padding-top.
             * @param {*} response
             */
            calculateHeaderHeight (response) {
                let height = 0

                const topHeight = this.topBar ? this.topBar.offsetHeight : 0
                const mainHeight = this.main.offsetHeight
                const accouncementHeight = this.announcementContainer ? this.announcementContainer.offsetHeight : 0
                const singleAnnouncementHeight = this.singleAnnouncement ? this.singleAnnouncement.offsetHeight : 0

                switch (response) {
                case 'drawer':
                    height = (topHeight + mainHeight)
                    break

                case 'collapse':
                    // add 44 for inaccurate calculation
                    height = (topHeight + mainHeight + singleAnnouncementHeight + accouncementHeight + 44)
                    break

                case 'reveal':
                    height = (topHeight + mainHeight + this.banners.offsetHeight)
                    break

                default:
                    height = section.offsetHeight
                    break
                }

                /**
                 * Set variable for future checks.
                 */
                this.section._currentHeight = height

                window.Frame.EventBus.emit('SiteHeader:height', (height))
                section.parentNode.style.height = `${height}px`
                return height
            },

            /**
             * Set listeners based on scroll direction.
             */
            setListeners () {
                on('resize', debounce(() => {
                    // this.refreshAnnouncementCarousel()
                    this.setBodyPadding()
                }, 250))

                window.Frame.EventBus.listen('SiteHeader:update', (response) => this.setBodyPadding(response))

                window.Frame.EventBus.listen('ScrollDirection:down', () => {
                    document.body.classList.add(cssClasses.navCollapsed)
                    section.parentNode.classList.add(cssClasses.collapsed)

                    // Overflow hidden after transition so it doesn't cut it off
                    window.setTimeout(() => {
                        section.parentNode.classList.add(cssClasses.overflow)
                        this.calculateHeaderHeight('collapse')
                    }, 250)
                })

                window.Frame.EventBus.listen('ScrollDirection:up', () => {
                    window.setTimeout(() => {
                        this.calculateHeaderHeight('reveal')
                    }, 250)
                })

                window.Frame.EventBus.listen([
                    'ScrollDirection:top',
                    'ScrollDirection:up'
                ], () => {
                    document.body.classList.remove(cssClasses.navCollapsed)
                    section.parentNode.classList.remove(cssClasses.collapsed)
                    section.parentNode.classList.remove(cssClasses.overflow)
                })
            }
        })
    }
}

export default SiteHeader



// WEBPACK FOOTER //
// ./src/resources/scripts/sections/SiteHeader.js